function HSMMDebounce(func, wait) {
    let timeout;
    return function (...args) {
        const context = this;
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(context, args), wait);
    };
}

const LG_BREAKPOINT = 1024, DEBOUNCE_DELAY = 250;

class HSMegaMenu extends HTMLElement {
    #megamenuIsVisible = this.dataset.showMegamenu === "true";

    #sectionId = this.dataset.sectionId;
    #section = document.querySelector(`#shopify-section-${this.#sectionId}`);

    #megamenus = document.querySelectorAll(".hs-megamenu");
    #megamenuOpenBtn = document.querySelector(".hs-megamenu-open");
    #megamenuCloseBtn = document.querySelector(".hs-megamenu-close");
    #megamenuMainLinks = this.querySelector(".hs-megamenu-main-links");

    #backdrop = document.querySelector(".hs-backdrop-megamenu");

    constructor() {
        super();

        if (!this.#megamenuIsVisible) return;

        // Store the bound event handlers in instance variables
        this.boundHandleMouseOver = this.#handleMouseOver.bind(this);
        this.boundHandleMouseLeave = this.#handleMouseLeave.bind(this);
    }

    connectedCallback() {
        // Handle scroll event
        this.#handleScroll();
        window.addEventListener("scroll", this.#handleScroll.bind(this));

        if (!this.#megamenuIsVisible) return;

        // Added this function because of error for IOS devices
        // resource https://stackoverflow.com/questions/17710893/why-when-do-i-have-to-tap-twice-to-trigger-click-on-ios
        this.#toggleMegaMenuListeners(window.innerWidth <= LG_BREAKPOINT);
        const handleResize = HSMMDebounce(() => this.#toggleMegaMenuListeners(window.innerWidth <= LG_BREAKPOINT), DEBOUNCE_DELAY);

        // Add the event listener for window resize
        window.addEventListener('resize', handleResize);

        // Handle Mega Menu Click
        this.addEventListener("click", this.#handleMegamenuClick.bind(this));

        // Handle Mega Menu open/close on mobile
        this.#megamenuOpenBtn.addEventListener("click", this.#handleMegamenuOpen.bind(this));
        this.#megamenuCloseBtn.addEventListener("click", this.#handleMegamenuClose.bind(this));

        // Handle backdrop click
        this.#backdrop.addEventListener("click", this.#handleMegamenuClose.bind(this));

        // Handle keyboard navigation for accessibility
        this.#megamenuMainLinks.addEventListener("keydown", this.#handleKeyboardNavigation.bind(this));
        window.addEventListener("keydown", this.#handleKeyboardNavigation.bind(this));
    }


    #toggleMegaMenuListeners(remove = false) {
        if (remove) {
            // Handle Mega Menu Open
            this.removeEventListener("mouseover", this.boundHandleMouseOver);
            // Handle Mega Menu Close
            this.#megamenus.forEach(megamenu => megamenu.removeEventListener("mouseleave", this.boundHandleMouseLeave));
        } else {
            // Handle Mega Menu Open
            this.addEventListener("mouseover", this.boundHandleMouseOver);
            // Handle Mega Menu Close
            this.#megamenus.forEach(megamenu => megamenu.addEventListener("mouseleave", this.boundHandleMouseLeave));

            // Close mobile megamenu if window size > 1024
            this.#handleMegamenuClose();
        }
    }

    #handleMouseOver(evt) {
        const el = evt.target;
        if (el.dataset.linkHandle) {
            this.#hideMegamenus(); // Chiudi tutti gli altri sottomenu
            this.#removeActiveLinkClass();
            this.#showMegamenu(el);
        }
    }

    #showMegamenu(link) {
        // this.#hideMegamenus(); // Chiudi tutti gli altri sottomenu
        // this.#removeActiveLinkClass();

        const linkHandle = link.dataset.linkHandle;
        const megamenu = document.querySelector(`.hs-megamenu[data-menu-handle="${linkHandle}"]`);
        if (!megamenu) return;

        link.classList.add("active");
        link.setAttribute("aria-expanded", "true");

        this.#section.classList.add("bg-white");
        this.#backdrop.classList.remove("hidden");
        megamenu.classList.remove("hidden");

        // Mostra tutti i link all'interno del sottomenu
        megamenu.querySelectorAll("li").forEach(li => li.classList.remove("max-lg:hidden"));
    }

    #hideMegamenus() {
        // La rimozione di bg-white NON appartiene a questa funzione: il suo
        // compito e' nascondere i sottomenu.
        // Da qui veniva il bug: #handleMegamenuOpen alla riga 170 aggiunge
        // bg-white e alla 171 chiama questa, che la togliеva due righe dopo.
        // Lo stesso accadeva sul tasto indietro alla riga 146.
        // Ora si toglie solo se il menu mobile NON e' aperto: lo stato e'
        // il pulsante di chiusura visibile, che #handleMegamenuOpen mostra
        // alla riga 168 e #handleMegamenuClose nasconde.
        if (this.#megamenuCloseBtn && this.#megamenuCloseBtn.classList.contains("hidden")) {
            this.#section.classList.remove("bg-white");
        }
        this.#megamenus.forEach(megamenu => {
            megamenu.classList.add("hidden"); // Nascondi il blocco del sottomenu
            megamenu.querySelectorAll(".active").forEach(activeLink => {
                activeLink.classList.remove("active");
                activeLink.setAttribute("aria-expanded", "false");
            });

            // Nascondi tutti i link all'interno del sottomenu
            megamenu.querySelectorAll("li").forEach(li => li.classList.add("max-lg:hidden"));
        });
        this.#backdrop.classList.add("hidden");
    }

    #handleMouseLeave(evt) {
        evt.currentTarget.classList.add("hidden")
        this.#backdrop.classList.add("hidden");
        //this.#section.classList.remove("bg-white");
        if (!document.body.hasAttribute('data-pq-menu-aperto')) {
            this.#section.classList.remove("bg-white");
        }
        this.#removeActiveLinkClass();
    }

    #handleScroll(evt) {
        // 50px scroll threshold
        const scrollAmount = window.scrollY || window.pageYOffset;
        if (scrollAmount > 50) this.#section.classList.remove("active");
        else this.#section.classList.add("active");
    }

    #handleMegamenuClick(evt) {
        const el = evt.target;

        if (el.dataset.linkHandle && el.tagName !== "A") return this.#handleMobileMegamenuOpen(el);
        if (el.dataset.level && el.tagName !== "A") return this.#handleSubmenuOpen(el);
        if (el.classList.contains("hs-megamenu-back")) return (() => {
            this.#hideMegamenus();
            this.#megamenuMainLinks.classList.remove("hidden");
        })();
    }

    #handleSubmenuOpen(link) {
        const level = link.dataset.level;

        let links = link.parentElement.querySelectorAll(`li[data-level="${level}"]`);

        link.setAttribute("data-closed", link.dataset.closed === "true" ? "false" : "true");

        links.forEach(li => li.classList.toggle("max-lg:hidden"));
    }

    #handleMegamenuOpen() {
        this.#megamenuCloseBtn.classList.remove("hidden");
        this.#megamenuOpenBtn.classList.add("hidden");
        this.#megamenuMainLinks.classList.remove("hidden");
        this.#megamenuMainLinks.classList.add("flex");
        this.#backdrop.classList.remove("hidden");
        this.#section.classList.add("bg-white");
        this.#hideMegamenus();
    }

    #handleMegamenuClose() {
        this.#megamenuCloseBtn.classList.add("hidden");
        this.#megamenuOpenBtn.classList.remove("hidden");
        this.#megamenuMainLinks.classList.add("hidden");
        this.#megamenuMainLinks.classList.remove("flex");
        this.#backdrop.classList.add("hidden");
        //this.#section.classList.remove("bg-white");
        this.#hideMegamenus();

        this.#removeActiveLinkClass();
    }

    #handleMobileMegamenuOpen(el) {
        this.#showMegamenu(el);
        this.#megamenuMainLinks.classList.add("hidden");
    }

    #removeActiveLinkClass() {
        const activeLink = this.querySelector(".hs-megamenu-main-link.active");
        activeLink?.classList.remove("active");
        activeLink?.setAttribute('aria-expanded', 'false');
    }

    #handleKeyboardNavigation(evt) {
        const el = evt.target;

        // Check if the Enter key is pressed
        if (evt.key === "Enter" && el.dataset.linkHandle) {
            evt.preventDefault(); // Prevent default behavior

            // Close all other submenus
            this.#hideMegamenus();

            // Open the submenu
            this.#showMegamenu(el);

            // Move focus to the first link in the submenu
            const linkHandle = el.dataset.linkHandle;
            const megamenu = document.querySelector(`.hs-megamenu[data-menu-handle="${linkHandle}"]`);
            const firstFocusable = megamenu?.querySelector('a');

            if (firstFocusable) {
                firstFocusable.focus();
            }
        }

        // Check if the Down Arrow key is pressed
        if (evt.key === "ArrowDown" && el.dataset.linkHandle) {
            evt.preventDefault(); // Prevent default behavior

            // Open the submenu if not already open
            this.#showMegamenu(el);

            // Find the first focusable element in the submenu
            const linkHandle = el.dataset.linkHandle;
            const megamenu = document.querySelector(`.hs-megamenu[data-menu-handle="${linkHandle}"]`);
            const firstFocusable = megamenu?.querySelector('a, button, [tabindex="0"]');

            // Move focus to the first focusable element in the submenu
            if (firstFocusable) {
                firstFocusable.focus();
            }
        }

        // Check if the Tab key is pressed
        if (evt.key === "Tab" && !evt.shiftKey) {
            const megamenu = el.closest('.hs-megamenu');
            if (megamenu) {
                const focusableElements = Array.from(megamenu.querySelectorAll('a, button, [tabindex="0"]'));
                const lastFocusable = focusableElements[focusableElements.length - 1];

                if (el === lastFocusable) {
                    evt.preventDefault(); // Prevent default behavior

                    // Move focus to the next parent menu link
                    const menuHandle = megamenu.getAttribute('data-menu-handle');
                    const parentMenuLink = document.querySelector(`[data-link-handle="${menuHandle}"]`);
                    const nextSibling = parentMenuLink?.nextElementSibling;

                    if (nextSibling && nextSibling.classList.contains('hs-megamenu-main-link')) {
                        nextSibling.focus();
                    }
                }
            }
        }

        // Check if the Esc key is pressed
        if (evt.key === "Escape") {
            evt.preventDefault(); // Prevent default behavior

            const megamenu = document.querySelector(`.hs-megamenu-main-link[aria-expanded="true"]`);

            if (!megamenu) return;
            megamenu.focus();

            // Close the currently open submenu
            this.#hideMegamenus();
        }

        // Check if the Up Arrow or Left Arrow key is pressed
        if (evt.key === "ArrowUp" || evt.key === "ArrowLeft") {
            evt.preventDefault(); // Prevent default behavior

            // Close the currently open submenu
            const megamenu = el.closest('.hs-megamenu');
            if (megamenu) {
                const menuHandle = megamenu.getAttribute('data-menu-handle');

                // Close the submenu
                this.#hideMegamenus();

                // Move focus back to the parent menu link
                const parentMenuLink = document.querySelector(`[data-link-handle="${menuHandle}"]`);
                if (parentMenuLink) {
                    parentMenuLink.focus();
                }
            }
        }
    }
}

customElements.define("hs-megamenu", HSMegaMenu);

// STATO DEL MENU MOBILE.
// Il pannello e' sempre nel DOM e nessuna classe lo marca come aperto:
// qui si crea quello stato sul body al click dell'hamburger, cosi' il
// mouseleave sopra sa che non deve togliere bg-white.
// 2026-10-04: lo stato e' un ATTRIBUTO del body (data-pq-menu-aperto), non
// una classe. Su questo tema ogni cambio di classe su <body> o <html> fa
// ricalcolare lo stile di tutta la pagina (3.000-3.500 elementi): in
// styles.css i selettori `[class~="not-prose"] *` di Tailwind Typography
// e `.menu[class*=" px-"] ... li > *` di daisyUI valgono per ogni
// discendente. Un attributo non li tocca: zero elementi ricalcolati.
// Nessun CSS e nessun altro script usava la classe.
(function () {
    var btn = document.querySelector('.hs-megamenu-open');
    if (!btn) return;

    var sec = btn.closest('[id^="shopify-section-"]');

    // ADD e non TOGGLE.
    // Il toggle sfasava lo stato: se la classe restava appesa da una
    // sessione precedente — cosa che accade perche' la chiusura dipende da
    // un click esterno che non sempre arriva — il click sull'hamburger la
    // TOGLIEVA invece di metterla, e da quel momento tutto era invertito.
    // Verificato nel log: lo stato risultava true col menu chiuso.
    btn.addEventListener('click', function () {
        document.body.setAttribute('data-pq-menu-aperto', '');
        if (sec) sec.classList.add('bg-white');
    });

    // Chiusura: solo un click FUORI dal pannello.
    // Prima il controllo lasciava passare i click sui link, e il pulsante
    // "indietro" della navigazione a livelli e' un link: entrando in una
    // voce e tornando indietro lo stato cadeva, e l'header ridiventava
    // trasparente col pannello ancora aperto.
    // Ora si chiude solo se il click e' del tutto esterno al nav e
    // all'hamburger: la navigazione interna, frecce e indietro compresi,
    // non tocca lo stato.
    document.addEventListener('click', function (e) {
        if (!document.body.hasAttribute('data-pq-menu-aperto')) return;
        // Il pulsante X chiude: e' dentro la section, quindi il controllo
        // sotto lo lascerebbe passare.
        if (e.target.closest('.hs-megamenu-close, [class*="close"]')) {
            document.body.removeAttribute('data-pq-menu-aperto');
            if (sec) sec.classList.remove('bg-white');
            return;
        }
        if (btn.contains(e.target)) return;
        // Il controllo passa dal nav alla SECTION.
        // Verificato: gli accordion del secondo livello — DETAILS.group e
        // SUMMARY.text-sm — stanno FUORI da nav.lg:static, quindi il
        // controllo sul nav li trattava come click esterni e chiudeva lo
        // stato. La section contiene tutto il megamenu a qualunque livello.
        if (sec && sec.contains(e.target)) return;
        document.body.removeAttribute('data-pq-menu-aperto');
        if (sec) sec.classList.remove('bg-white');
    });
})();